(() => {
  const intervalMs = 6500;
  const swipeThreshold = 50;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const cleanups = new WeakMap();

  function initializeHome(scope = document) {
    const home = scope.querySelector('[data-cb-demo-home]');
    if (!home || cleanups.has(home)) return;
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const hero = home.querySelector('[data-cb-home-carousel]');
    const slides = [...home.querySelectorAll('[data-cb-slide]')];
    const counter = home.querySelector('.slide-counter');
    const pause = home.querySelector('#hero-pause');
    let active = 0;
    let timer;
    let userPaused = reducedMotion.matches;
    let pointerInside = false;

    const stop = () => { clearInterval(timer); timer = undefined; };
    const rotationAllowed = () => !userPaused && !pointerInside && !hero.contains(document.activeElement) && !document.hidden;
    const resume = () => {
      stop();
      if (rotationAllowed()) timer = setInterval(() => showSlide(active + 1), intervalMs);
    };
    function showSlide(index) {
      active = (index + slides.length) % slides.length;
      slides.forEach((slide, position) => {
        // Keep exactly one visible H1 as the selected banner changes.
        const heading = slide.querySelector('h1,h2');
        const tag = position === active ? 'h1' : 'h2';
        if (heading.tagName.toLowerCase() !== tag) {
          const replacement = document.createElement(tag);
          replacement.append(...heading.childNodes);
          heading.replaceWith(replacement);
        }
        slide.hidden = position !== active;
        slide.setAttribute('aria-hidden', String(position !== active));
        slide.classList.toggle('is-active', position === active);
      });
      counter.textContent = `${String(active + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
      counter.setAttribute('aria-live', timer ? 'off' : 'polite');
    }
    home.querySelector('#hero-prev').addEventListener('click', () => { showSlide(active - 1); resume(); }, options);
    home.querySelector('#hero-next').addEventListener('click', () => { showSlide(active + 1); resume(); }, options);
    pause.addEventListener('click', () => {
      userPaused = !userPaused;
      pause.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
      pause.setAttribute('aria-pressed', String(userPaused));
      pause.querySelector('use').setAttribute('href', userPaused ? '#cb-demo-icon-play' : '#cb-demo-icon-pause');
      resume();
    }, options);
    pause.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
    pause.setAttribute('aria-pressed', String(userPaused));
    if (userPaused) pause.querySelector('use').setAttribute('href', '#cb-demo-icon-play');
    hero.addEventListener('mouseenter', () => { pointerInside = true; stop(); }, options);
    hero.addEventListener('mouseleave', () => { pointerInside = false; resume(); }, options);
    hero.addEventListener('focusin', stop, options);
    hero.addEventListener('focusout', () => requestAnimationFrame(resume), options);
    hero.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      showSlide(active + (event.key === 'ArrowRight' ? 1 : -1));
    }, options);
    let touch;
    hero.addEventListener('touchstart', event => { const point = event.touches[0]; touch = { x: point.clientX, y: point.clientY }; stop(); }, { ...options, passive: true });
    hero.addEventListener('touchend', event => {
      if (!touch) return;
      const point = event.changedTouches[0];
      const dx = point.clientX - touch.x;
      const dy = point.clientY - touch.y;
      if (Math.abs(dx) > swipeThreshold && Math.abs(dx) > Math.abs(dy)) showSlide(active + (dx < 0 ? 1 : -1));
      touch = undefined;
      resume();
    }, { ...options, passive: true });
    document.addEventListener('visibilitychange', resume, options);
    reducedMotion.addEventListener('change', () => {
      if (reducedMotion.matches) {
        userPaused = true;
        pause.setAttribute('aria-label', 'Play slideshow');
        pause.setAttribute('aria-pressed', 'true');
        pause.querySelector('use').setAttribute('href', '#cb-demo-icon-play');
      }
      resume();
    }, options);
    resume();

    home.querySelectorAll('[data-cb-rail]').forEach(button => {
      const rail = home.querySelector('#' + CSS.escape(button.dataset.cbRail));
      if (!rail) return;
      button.setAttribute('aria-controls', rail.id);
      const update = () => { button.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2; };
      button.addEventListener('click', () => { rail.scrollBy({ left: rail.clientWidth * .85, behavior: reducedMotion.matches ? 'instant' : 'smooth' }); }, options);
      rail.addEventListener('scroll', update, { ...options, passive: true });
      window.addEventListener('resize', update, options);
      update();
    });

    const tips = [
      { title: 'Start with your set number.', text: 'Find the number on your LEGO® box or instruction booklet. Use it to discover the display options made for your model.', label: 'Find my set', url: '/pages/find-your-display', icon: 'fit' },
      { title: 'Make room for your display.', text: 'Measure the shelf or wall space before you choose. Check the finished display dimensions and assembly information on the product page.', label: 'Read the sizing guide', url: '/blogs/guides/measure-shelf-for-lego-display-case', icon: 'fit' },
      { title: 'Choose your point of view.', text: 'A wall display and a freestanding case offer different ways to enjoy your collection. Start with your room and your viewing angle.', label: 'Compare display formats', url: '/blogs/guides/f1-wall-display-vs-acrylic-case', icon: 'fit' }
    ];
    const guide = home.querySelector('.guide-carousel');
    let currentTip = 0;
    function showTip(direction) {
      currentTip = (currentTip + direction + tips.length) % tips.length;
      const tip = tips[currentTip];
      guide.querySelector('h3').textContent = tip.title;
      guide.querySelector('.guide-copy p').textContent = tip.text;
      const link = guide.querySelector('.guide-copy a');
      link.textContent = tip.label;
      link.href = tip.url;
      guide.querySelector('.guide-symbol span').textContent = `${String(currentTip + 1).padStart(2, '0')} / 03`;
      guide.querySelector('.guide-symbol use').setAttribute('href', '#cb-demo-icon-' + tip.icon);
    }
    guide.querySelector('.guide-prev').addEventListener('click', () => showTip(-1), options);
    guide.querySelector('.guide-next').addEventListener('click', () => showTip(1), options);
    cleanups.set(home, () => { stop(); controller.abort(); cleanups.delete(home); });
  }

  initializeHome();
  document.addEventListener('shopify:section:load', event => initializeHome(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const home = event.target.querySelector('[data-cb-demo-home]');
    cleanups.get(home)?.();
  });
})();
